import React, { useState } from 'react'

import {
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    Modal,
    Paper,
    Slide,
    Switch,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { Container } from '../../../../components'
import {
    Area,
    AreaConfig,
    AreaStatusConfig,
    AreaStatusTag,
    BaseWorkorder,
    Folder,
    IdBoolMap,
    Lease,
    ListVendor,
    ModelListMap,
    ModelMap,
    Schedule,
    Service,
    UnitConfig,
} from '../../../../models'
import { FinderSelection, useDateRange } from '../../../../hooks'
import {
    AssignUnitDict,
    CreateWorkorderActionThunk,
    CreateWorkOrderRequest,
} from '../../../../store'
import { ChooseServiceAreas } from './ChooseServiceAreas'
import { SelectDetails } from './SelectDetails'
import { toast } from 'react-toastify'
import { VersionedTree } from '../../../../hooks/useInfrastructure/types'
import { useTimezone } from '../../../../contexts/timezone/timezone'
import { DateTime } from 'luxon'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    /** When false, renders as a full modal. When true, renders only the inner content for embedding in another modal. */
    embedded?: boolean
    open?: boolean
    filteredTree: VersionedTree
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    areaLeaseMap: ModelListMap<Lease>
    serviceList?: Service[]
    vendorList?: ListVendor[]
    schedule?: Schedule
    serviceAreaMap: IdBoolMap
    assignmentFinderSelection: FinderSelection
    areaStatusTagMap: ModelMap<AreaStatusTag>
    areaStatusConfigList: AreaStatusConfig[]
    createWorkorder: (req: CreateWorkOrderRequest) => CreateWorkorderActionThunk
    isAreaInFilter: (area: Area) => boolean
    onClose: () => void
}

const HEADER_FOOTER_HEIGHT = 64

export const SchedulerAssignModal = (props: Props) => {
    const {
        embedded = false,
        open = true,
        onClose,
        filteredTree,
        serviceList,
        unitConfigMap,
        unitWorkorderMap,
        areaLeaseMap,
        schedule,
        areaConfigMap,
        vendorList,
        serviceAreaMap,
        createWorkorder,
        assignmentFinderSelection,
        isAreaInFilter,
        areaStatusTagMap,
    } = props
    const RANGE_KEY = 'ASSIGN_DATE_RANGE'
    const theme = useTheme()
    const MARGIN = theme.spacing(4)
    const MODAL_HEIGHT = window.innerHeight - 2 * MARGIN
    const MODAL_WIDTH = window.innerWidth - 2 * MARGIN

    const [includeDates, setIncludeDates] = useState(true)

    const { currentTimezone } = useTimezone()

    const { dateRange, setRange } = useDateRange({
        rangeKey: RANGE_KEY,
        color: includeDates
            ? theme.palette.primary.main
            : theme.palette.grey[500],
        timezone: currentTimezone,
    })

    const [selectedServiceId, setSelectedServiceId] = useState(-1)
    const [selectedVendorId, setSelectedVendorId] = useState(-1)

    const [subToBedStatus, setSubToBedStatus] = useState(true)
    const [createLoading, setCreateLoading] = useState(false)

    const [areaSelection, setAreaSelection] = useState<IdBoolMap>({})

    const canCreate =
        assignmentFinderSelection.selection.unit.length > 0 &&
        selectedServiceId !== -1 &&
        selectedVendorId !== -1

    const handleCreate = (exitAfterCreate: boolean) => {
        if (!dateRange.startDate || !dateRange.endDate || !schedule) {
            return
        }
        const startDate = DateTime.fromJSDate(dateRange.startDate)
            .setZone(currentTimezone, { keepLocalTime: true })
            .startOf('day')
        const endDate = DateTime.fromJSDate(dateRange.endDate)
            .setZone(currentTimezone, { keepLocalTime: true })
            .endOf('day')
        let startDateStr = startDate.toUTC().toISO()
        let endDateStr = endDate.toUTC().toISO()
        if (!includeDates) {
            startDateStr = null
            endDateStr = null
        }
        const assignUnits: AssignUnitDict[] = []
        const visitFolder = (folder: Folder) => {
            folder.children.forEach(visitFolder)
            folder.units.forEach((treeUnit) => {
                const unit =
                    assignmentFinderSelection.selection.unit[treeUnit.id]
                if (unit === undefined) return
                const assignUnitDict: AssignUnitDict = {
                    unit_id: unit.id,
                    service_areas: [],
                }
                unit.areas.forEach((area) => {
                    if (areaSelection[area.id] === true) {
                        assignUnitDict.service_areas.push(area.id)
                    }
                })
                assignUnits.push(assignUnitDict)
            })
        }
        visitFolder(filteredTree.root)
        setCreateLoading(true)
        createWorkorder({
            body: {
                start_date: startDateStr ?? undefined,
                end_date: endDateStr ?? undefined,
                vendor: selectedVendorId !== -1 ? selectedVendorId : undefined,
                service: selectedServiceId,
                schedule: schedule.id,
                units: assignUnits,
                subscribe_to_bed_status_change: subToBedStatus,
                creation_source: 'BULK_ASSIGN_WEB',
            },
        })
            .then((res) => {
                toast.success(
                    `Success! ${res.data.created_count} workorders created.`,
                )
                if (exitAfterCreate) {
                    onClose()
                }
            })
            .finally(() => {
                setCreateLoading(false)
            })
    }

    const assignContent = (
        <Container
            style={{
                ...(embedded
                    ? {
                          flex: 1,
                          minHeight: 0,
                          width: '100%',
                          alignSelf: 'stretch',
                          flexDirection: 'column',
                          display: 'flex',
                      }
                    : {
                          height: MODAL_HEIGHT,
                          width: MODAL_WIDTH,
                          marginTop: MARGIN,
                          marginBottom: MARGIN,
                          flexDirection: 'column',
                      }),
            }}
        >
            {/* Header */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    height: HEADER_FOOTER_HEIGHT,
                    alignItems: 'center',
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h5),
                        fontWeight: 700,
                    }}
                >
                    Assign Services
                </span>

                <FormControlLabel
                    control={
                        <Switch
                            checked={subToBedStatus}
                            color="primary"
                            onChange={() => {
                                setSubToBedStatus(!subToBedStatus)
                            }}
                        />
                    }
                    labelPlacement="end"
                    label={
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                            }}
                        >
                            Subscribe to bed status changes (workorders will be
                            unlocked when on)
                        </span>
                    }
                />

                <div style={{ flex: 1 }} />

                {!embedded && (
                    <Tooltip title="Cancel">
                        <IconButton onClick={onClose}>
                            <CloseIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>
            <Divider />

            {/* Body */}
            <Container style={{ flex: 1, minHeight: 0 }} scrollY>
                <SelectDetails
                    prunedTree={filteredTree.root}
                    assignmentFinderSelection={assignmentFinderSelection}
                    unitWorkorderMap={unitWorkorderMap}
                    includeDates={includeDates}
                    setIncludeDates={setIncludeDates}
                    dateRange={dateRange}
                    setDateRange={setRange}
                    serviceList={serviceList}
                    selectedServiceId={selectedServiceId}
                    setSelectedServiceId={setSelectedServiceId}
                    rangeKey={RANGE_KEY}
                    vendorList={vendorList}
                    selectedVendorId={selectedVendorId}
                    setSelectedVendorId={setSelectedVendorId}
                    clearLocationSelection={() =>
                        assignmentFinderSelection.setFinderSelection()
                    }
                    areaConfigMap={areaConfigMap}
                    areaStatusTagMap={areaStatusTagMap}
                />

                <ChooseServiceAreas
                    tree={filteredTree.root}
                    areaStatusTagMap={props.areaStatusTagMap}
                    areaStatusConfigList={props.areaStatusConfigList}
                    assignmentFinderSelection={assignmentFinderSelection}
                    unitConfigMap={unitConfigMap}
                    unitWorkorderMap={unitWorkorderMap}
                    areaLeaseMap={areaLeaseMap}
                    schedule={schedule}
                    areaConfigMap={areaConfigMap}
                    serviceAreaMap={serviceAreaMap}
                    isAreaInFilter={isAreaInFilter}
                    assignTimeline={{
                        startDate: dateRange.startDate ?? new Date(),
                        endDate: dateRange.endDate ?? new Date(),
                    }}
                    includeTimeline={includeDates}
                    useLeasesToCalculateServiceArea={false}
                    areaSelection={areaSelection}
                    setAreaSelection={setAreaSelection}
                />
            </Container>
            <Divider />

            {/* Footer */}
            <Container
                style={{
                    height: HEADER_FOOTER_HEIGHT,
                    padding: theme.spacing(1),
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                }}
            >
                <div style={{ flex: 1 }} />

                {createLoading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!canCreate}
                            onClick={() => handleCreate(false)}
                        >
                            Create and keep working (
                            {assignmentFinderSelection.selection.unit.length})
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canCreate}
                            onClick={() => handleCreate(true)}
                        >
                            Create and exit (
                            {assignmentFinderSelection.selection.unit.length})
                        </Button>
                    </>
                )}
            </Container>
        </Container>
    )

    if (embedded) {
        return assignContent
    }

    return (
        <Modal open={open} onClose={onClose} keepMounted>
            <Slide in={open} direction="up">
                <Paper style={{ marginLeft: MARGIN, marginRight: MARGIN }}>
                    {assignContent}
                </Paper>
            </Slide>
        </Modal>
    )
}
